import React from 'react';
import { TFunction } from 'react-i18next';
import {
  Text,
  Link,
  CodeSnippet,
  Stack,
  Trans,
  OrderedList,
  CodeInline,
} from '@piwikpro/ui-components';
import { ProductAnalytics } from '@piwikpro/product-analytics';
import { App, ConsentManagementTool } from '../../../model';

const CI_CMP_SNIPPET = '<script id="CookieConsent" src="https://policy.app.cookieinformation.com/uc.js" data-culture="EN" data-gcm-version="2.0" type="text/javascript"></script>';

const PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES = {
  MANUAL_ASYNC: '@platform/command/appInstallation/manualAsync',
};

interface ManualInstallProps {
  app: App
  t: TFunction
  installationCodeFailed: boolean
  productAnalytics: ProductAnalytics
  snippet: string
  cmp: ConsentManagementTool | null
  handleCopyToClipboard: (result: boolean, productAnalyticsActionType: string) => void
}

const ManualInstall: React.FC<ManualInstallProps> = ({
  app,
  t,
  installationCodeFailed,
  productAnalytics,
  snippet,
  cmp,
  handleCopyToClipboard,
}) => (
  <Stack vertical>
    <Stack.Item previousSpacing="none">
      <OrderedList>
        <Stack vertical spacing="narrow">
          <Stack.Item previousSpacing="wide">
            <OrderedList.Item>
              <Text as="p">
                {t('apps.details.installation.async.instruction.line1-part1')}
                <b> &lt;body&gt; </b>
                {t('apps.details.installation.async.instruction.line1-part2')}
              </Text>
            </OrderedList.Item>
          </Stack.Item>
          <Stack.Item fill>
            <CodeSnippet
              error={installationCodeFailed}
              code={snippet && !installationCodeFailed ? snippet : undefined}
              onCopy={result => handleCopyToClipboard(
                result,
                PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.MANUAL_ASYNC,
              )}
              mode="no-highlight"
            />
          </Stack.Item>
          {cmp === 'CookieInformationCMP' && (
            <>
              <Stack.Item>
                <OrderedList.Item>
                  <Text as="p">
                    {t('apps.details.installation.async.instruction.line2-ci-part1')}
                    <b> &lt;head&gt; </b>
                    {t('apps.details.installation.async.instruction.line2-ci-part2')}
                  </Text>
                </OrderedList.Item>
              </Stack.Item>
              <Stack.Item fill>
                <CodeSnippet
                  error={installationCodeFailed}
                  code={CI_CMP_SNIPPET}
                  onCopy={result => handleCopyToClipboard(
                    result,
                    PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.MANUAL_ASYNC,
                  )}
                  mode="no-highlight"
                />
              </Stack.Item>
              <Stack.Item fill previousSpacing="narrow">
                <Text as="p" size={12} dataId="Caption" color="InkLighter">
                  <Trans
                    i18nKey="apps.details.installation.async.language-instruction.ci-caption"
                    components={[
                      <Link
                        size="small"
                        onClick={() => {
                          productAnalytics.push({ type: `${PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.MANUAL_ASYNC}/tracker-debugger` });
                        }}
                        href="https://support.cookieinformation.com/en/articles/5444177-set-up-the-consent-popup-script"
                      >
                        here
                      </Link>,
                      <CodeInline dataId="TextStyle">data-culture</CodeInline>,
                    ]}
                  />
                </Text>
              </Stack.Item>
            </>
          )}
          <Stack.Item>
            <OrderedList.Item>
              {t('apps.details.installation.async.instruction.line2.part1')}
              <Link
                onClick={() => {
                  productAnalytics.push({ type: `${PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.MANUAL_ASYNC}/tracker-debugger` });
                }}
                href={`/analytics/#/${app.id}/settings/debugger/`}
              >
                {t('apps.details.installation.links.trackerDebugger')}
              </Link>
              {t('apps.details.installation.async.instruction.line2.part2')}
            </OrderedList.Item>
          </Stack.Item>
          {cmp === 'OtherConsentTool' && (
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  t={t}
                  i18nKey="apps.details.installation.async.instruction.line2-caption"
                  components={[
                    <Link href="https://help.piwik.pro/support/privacy/how-can-i-integrate-a-third-party-cookie-consent-tool-with-piwik-pro/">
                      installation guide
                    </Link>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
          )}
          {cmp === 'PiwikLegacy' && (
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.async.instruction.line3.part1')}
                <Link href="https://help.piwik.pro/support/consent-manager/update-link-privacy-policy-page/">
                  {t('apps.details.installation.links.privacyPolicyAddress')}
                </Link>
                {t('apps.details.installation.async.instruction.line3.part2')}
                <Link href="https://help.piwik.pro/support/consent-manager/setting-consent-manager/">
                  {t('apps.details.installation.links.settingsConsent')}
                </Link>
                .
              </OrderedList.Item>
            </Stack.Item>
          )}
          <Stack.Item previousSpacing="wide" />
        </Stack>
      </OrderedList>
    </Stack.Item>
    <Stack.Item previousSpacing="none">
      <Text as="p">
        {t('apps.details.installation.instruction.help.part1')}
        <Link href="https://help.piwik.pro/support/getting-started/install-a-tracking-code/">
          {t('apps.details.installation.links.installGuide')}
        </Link>
        {t('apps.details.installation.instruction.help.part2')}
        <Link href="http://community.piwik.pro/">
          {t('apps.details.installation.links.community')}
        </Link>
        .
      </Text>
    </Stack.Item>

  </Stack>
);

export default ManualInstall;
